/**
 * Offline Queue Service
 *
 * Zentrale Queue-Verwaltung für Offline-Operationen mit:
 * - Automatische Sync bei Online-Status
 * - Retry-Logik mit Exponential Backoff
 * - Konflikt-Detection
 * - Event-System für UI-Updates
 */

import { offlineDb, STORES } from './offlineDatabase';

// Operation Types
export const OperationType = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE'
};

// Entity Types
export const EntityType = {
  SHOPPING_LIST: 'SHOPPING_LIST',
  SHOPPING_LIST_ITEM: 'SHOPPING_LIST_ITEM',
  RECIPE: 'RECIPE',
  RECIPE_INGREDIENT: 'RECIPE_INGREDIENT',
  BOOKMARK: 'BOOKMARK'
};

// Queue Status
export const QueueStatus = {
  PENDING: 'PENDING',
  SYNCING: 'SYNCING',
  SYNCED: 'SYNCED',
  CONFLICT: 'CONFLICT',
  FAILED: 'FAILED'
};

// Retry delays in ms (Exponential Backoff)
const RETRY_DELAYS = [1000, 5000, 15000, 30000, 60000];
const MAX_RETRIES = 5;

class OfflineQueueService {
  constructor() {
    this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.isSyncing = false;
    this.syncListeners = new Set();
    this.apiHandlers = new Map();

    // Browser Event Listener für Online/Offline
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.handleOnline());
      window.addEventListener('offline', () => this.handleOffline());
    }
  }

  /**
   * Registriert API-Handler für Entity-Typen
   * @param {string} entityType
   * @param {object} handlers - { create, update, delete, getById }
   */
  registerApiHandler(entityType, handlers) {
    this.apiHandlers.set(entityType, handlers);
  }

  /**
   * Fügt eine Operation zur Queue hinzu
   * @param {object} operation
   * @returns {Promise<object>} Das Queue-Item
   */
  async enqueue(operation) {
    // Bei DELETE: Prüfen ob es eine ausstehende CREATE für dasselbe Item gibt
    if (operation.operation === OperationType.DELETE) {
      const cancelledCount = await this.cancelConflictingCreates(operation);
      if (cancelledCount > 0) {
        console.log(`[OfflineQueue] ${cancelledCount} konfliktierendes CREATE(s) storniert für DELETE`);
      }
    }

    const queueItem = {
      ...operation,
      timestamp: Date.now(),
      status: QueueStatus.PENDING,
      retryCount: 0,
      tempId: operation.tempId || `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };

    // In IndexedDB speichern
    const id = await offlineDb.add(STORES.QUEUE, queueItem);
    queueItem.id = id;

    console.log('[OfflineQueue] Operation eingereiht:', queueItem);
    this.notifyListeners({ type: 'QUEUE_UPDATED', item: queueItem });

    // Sofortige Sync wenn online
    if (this.isOnline && !this.isSyncing) {
      // Verzögert ausführen um UI-Update zu ermöglichen
      setTimeout(() => this.processQueue(), 100);
    }

    return queueItem;
  }

  /**
   * Storniert ausstehende CREATE-Operationen die mit einer DELETE-Operation kollidieren
   * @param {object} deleteOperation - Die DELETE-Operation
   * @returns {Promise<number>} Anzahl stornierter Operationen
   */
  async cancelConflictingCreates(deleteOperation) {
    const allItems = await offlineDb.getAll(STORES.QUEUE);
    let cancelledCount = 0;

    for (const item of allItems) {
      if (item.status !== QueueStatus.PENDING && item.status !== QueueStatus.FAILED) {
        continue;
      }

      if (item.operation !== OperationType.CREATE) {
        continue;
      }

      // Prüfe ob es dasselbe Item betrifft
      const isConflicting = this.isConflictingOperation(deleteOperation, item);

      if (isConflicting) {
        console.log('[OfflineQueue] Storniere konfliktierende CREATE-Operation:', item.id);
        await offlineDb.delete(STORES.QUEUE, item.id);
        this.notifyListeners({ type: 'ITEM_CANCELLED', item, reason: 'Superseded by DELETE' });
        cancelledCount++;
      }
    }

    return cancelledCount;
  }

  /**
   * Prüft ob zwei Operationen dasselbe Item betreffen
   */
  isConflictingOperation(deleteOp, createOp) {
    // Für Shopping List Items: Vergleiche listId und item name
    if (deleteOp.entityType === EntityType.SHOPPING_LIST_ITEM &&
        createOp.entityType === EntityType.SHOPPING_LIST_ITEM) {
      const deleteListId = String(deleteOp.payload?.listId || '');
      const createListId = String(createOp.payload?.listId || '');

      if (deleteListId !== createListId) {
        return false;
      }

      // Vergleiche Item-Namen - nur stornieren wenn Namen übereinstimmen
      const deleteItemName = deleteOp.payload?.itemName;
      const createItemName = createOp.payload?.item?.name;

      // Nur konfliktierend wenn beide Namen vorhanden sind UND übereinstimmen
      if (deleteItemName && createItemName) {
        const namesMatch = deleteItemName.toLowerCase().trim() === createItemName.toLowerCase().trim();
        if (namesMatch) {
          console.log(`[OfflineQueue] Konflikt erkannt: DELETE und CREATE für "${deleteItemName}"`);
          return true;
        }
      }

      // Ohne Item-Namen können wir keinen sicheren Vergleich machen
      return false;
    }

    // Für Shopping Lists: Vergleiche entityId/tempId
    if (deleteOp.entityType === EntityType.SHOPPING_LIST &&
        createOp.entityType === EntityType.SHOPPING_LIST) {
      const deleteId = String(deleteOp.entityId || '');
      const createTempId = String(createOp.tempId || '');
      return deleteId === createTempId;
    }

    // Für Recipes: Vergleiche entityId/tempId
    if (deleteOp.entityType === EntityType.RECIPE &&
        createOp.entityType === EntityType.RECIPE) {
      const deleteId = String(deleteOp.entityId || '');
      const createTempId = String(createOp.tempId || '');
      return deleteId === createTempId;
    }

    return false;
  }

  /**
   * Verarbeitet alle ausstehenden Queue-Items
   */
  async processQueue() {
    if (this.isSyncing || !this.isOnline) {
      console.log('[OfflineQueue] Sync übersprungen - isSyncing:', this.isSyncing, 'isOnline:', this.isOnline);
      return;
    }

    this.isSyncing = true;
    this.notifyListeners({ type: 'SYNC_STARTED' });

    try {
      const allItems = await offlineDb.getAll(STORES.QUEUE);
      const pendingItems = allItems.filter(item =>
        item.status === QueueStatus.PENDING || item.status === QueueStatus.FAILED
      );

      if (pendingItems.length === 0) {
        console.log('[OfflineQueue] Keine ausstehenden Items');
        return;
      }

      console.log('[OfflineQueue] Verarbeite', pendingItems.length, 'Items');

      // Sortieren: DELETEs zuerst, dann CREATEs, dann UPDATEs
      const sorted = this.sortByPriority(pendingItems);

      for (const item of sorted) {
        try {
          await this.processItem(item);
        } catch (error) {
          console.error('[OfflineQueue] Fehler bei Item:', item.id, error);
          await this.handleSyncError(item, error);
        }
      }
    } finally {
      this.isSyncing = false;
      this.notifyListeners({ type: 'SYNC_COMPLETED' });
    }
  }

  /**
   * Sortiert Items nach Priorität
   */
  sortByPriority(items) {
    const priority = {
      [OperationType.DELETE]: 0,
      [OperationType.CREATE]: 1,
      [OperationType.UPDATE]: 2
    };

    return [...items].sort((a, b) => {
      const pA = priority[a.operation] ?? 3;
      const pB = priority[b.operation] ?? 3;
      if (pA !== pB) return pA - pB;
      // Bei gleicher Priorität: Ältere zuerst
      return a.timestamp - b.timestamp;
    });
  }

  /**
   * Verarbeitet ein einzelnes Queue-Item
   */
  async processItem(item) {
    // Status auf SYNCING setzen
    await offlineDb.put(STORES.QUEUE, { ...item, status: QueueStatus.SYNCING });
    this.notifyListeners({ type: 'ITEM_SYNCING', item });

    // API-Handler holen
    const handler = this.apiHandlers.get(item.entityType);
    if (!handler) {
      throw new Error(`Kein API-Handler für EntityType: ${item.entityType}`);
    }

    let result;

    switch (item.operation) {
      case OperationType.CREATE:
        result = await handler.create(item.payload);
        // Temp-ID zu echter ID mappen
        if (result?.id && item.tempId) {
          await this.updateTempIdReferences(item.tempId, result.id, item.entityType);
        }
        break;

      case OperationType.UPDATE:
        // Konflikt-Check
        if (handler.getById && item.originalUpdatedAt) {
          const conflict = await this.detectConflict(item, handler);
          if (conflict.hasConflict) {
            await offlineDb.put(STORES.QUEUE, {
              ...item,
              status: QueueStatus.CONFLICT,
              conflict: conflict
            });
            this.notifyListeners({ type: 'CONFLICT_DETECTED', item, conflict });
            return;
          }
        }
        result = await handler.update(item.entityId, item.payload);
        break;

      case OperationType.DELETE:
        result = await handler.delete(item.entityId);
        break;

      default:
        throw new Error(`Unbekannte Operation: ${item.operation}`);
    }

    // Als SYNCED markieren
    await offlineDb.put(STORES.QUEUE, { ...item, status: QueueStatus.SYNCED, result });
    this.notifyListeners({ type: 'ITEM_SYNCED', item, result });

    // Nach kurzer Zeit aus Queue entfernen
    setTimeout(async () => {
      await offlineDb.delete(STORES.QUEUE, item.id);
    }, 5000);

    return result;
  }

  /**
   * Prüft auf Konflikte mit Server-Version
   */
  async detectConflict(item, handler) {
    try {
      const serverVersion = await handler.getById(item.entityId);

      if (serverVersion && item.originalUpdatedAt) {
        const serverTime = new Date(serverVersion.updatedAt).getTime();
        const originalTime = new Date(item.originalUpdatedAt).getTime();

        if (serverTime > originalTime) {
          return {
            hasConflict: true,
            serverVersion,
            localVersion: item.payload,
            serverUpdatedAt: serverVersion.updatedAt,
            localUpdatedAt: item.originalUpdatedAt
          };
        }
      }

      return { hasConflict: false };
    } catch (error) {
      // Bei Fehler (z.B. 404) kein Konflikt
      return { hasConflict: false };
    }
  }

  /**
   * Aktualisiert Referenzen von Temp-ID zu echter ID
   */
  async updateTempIdReferences(tempId, realId, entityType) {
    // In anderen Queue-Items aktualisieren
    const allItems = await offlineDb.getAll(STORES.QUEUE);
    for (const item of allItems) {
      let updated = false;

      if (item.parentId === tempId) {
        item.parentId = realId;
        updated = true;
      }
      if (item.entityId === tempId) {
        item.entityId = realId;
        updated = true;
      }

      // Update payload.listId for shopping list items
      if (item.payload?.listId === tempId) {
        item.payload.listId = realId;
        updated = true;
      }

      if (updated) {
        console.log('[OfflineQueue] Updated temp ID reference:', tempId, '->', realId, 'in item:', item.id);
        await offlineDb.put(STORES.QUEUE, item);
      }
    }

    // Im Cache aktualisieren
    const cacheStore = this.getCacheStoreForEntity(entityType);
    if (cacheStore) {
      const tempItem = await offlineDb.get(cacheStore, tempId);
      if (tempItem) {
        await offlineDb.delete(cacheStore, tempId);
        await offlineDb.put(cacheStore, { ...tempItem, id: realId, _isTemp: false });
      }
    }
  }

  /**
   * Ermittelt den Cache-Store für einen Entity-Typ
   */
  getCacheStoreForEntity(entityType) {
    switch (entityType) {
      case EntityType.SHOPPING_LIST:
      case EntityType.SHOPPING_LIST_ITEM:
        return STORES.SHOPPING_LISTS;
      case EntityType.RECIPE:
      case EntityType.RECIPE_INGREDIENT:
        return STORES.RECIPES;
      case EntityType.BOOKMARK:
        return STORES.BOOKMARKS;
      default:
        return null;
    }
  }

  /**
   * Prüft ob ein Fehler "idempotent" ist (Operation war im Grunde erfolgreich)
   * z.B. "existiert bereits" bei CREATE - das Item ist ja da, also Ziel erreicht
   */
  isIdempotentSuccess(error, item) {
    const errorMessage = error?.message || '';

    // "existiert bereits" bei CREATE = Item ist schon da = Erfolg
    if (item.operation === OperationType.CREATE) {
      if (errorMessage.includes('existiert bereits') ||
          errorMessage.includes('already exists') ||
          errorMessage.includes('duplicate')) {
        console.log('[OfflineQueue] Idempotent success: Item already exists (CREATE treated as success)');
        return true;
      }
    }

    // "nicht gefunden" bei DELETE = Item ist schon weg = Erfolg
    if (item.operation === OperationType.DELETE) {
      const status = error?.response?.status || error?.status;
      if (status === 404 ||
          errorMessage.includes('nicht gefunden') ||
          errorMessage.includes('not found')) {
        console.log('[OfflineQueue] Idempotent success: Item not found (DELETE treated as success)');
        return true;
      }
    }

    return false;
  }

  /**
   * Prüft ob ein Fehler "permanent" ist (kein Retry sinnvoll)
   */
  isPermanentError(error, item) {
    const status = error?.response?.status || error?.status;

    // 404: Entity existiert nicht auf Server
    // 500 mit temp-ID: Parent wurde nie synchronisiert
    if (status === 404) {
      console.log('[OfflineQueue] Permanent error: Entity not found (404)');
      return true;
    }

    // Prüfe ob parentId oder listId noch temp-ID ist
    const hasOrphanedTempId =
      (item.parentId && String(item.parentId).startsWith('temp-')) ||
      (item.payload?.listId && String(item.payload.listId).startsWith('temp-')) ||
      (item.entityId && String(item.entityId).startsWith('temp-'));

    if (hasOrphanedTempId) {
      console.log('[OfflineQueue] Permanent error: Orphaned temp-ID reference');
      return true;
    }

    // 500 bei Shopping List Items könnte bedeuten, dass die Liste gelöscht wurde
    if (status === 500 && item.entityType === EntityType.SHOPPING_LIST_ITEM) {
      console.log('[OfflineQueue] Permanent error: Shopping list item failed (parent may be deleted)');
      return true;
    }

    return false;
  }

  /**
   * Behandelt Sync-Fehler mit Retry-Logik
   */
  async handleSyncError(item, error) {
    // Prüfe ob es eigentlich ein idempotenter Erfolg ist
    // z.B. "existiert bereits" bei CREATE = Item ist ja da = Erfolg
    if (this.isIdempotentSuccess(error, item)) {
      console.log('[OfflineQueue] Idempotent success - marking as SYNCED:', item.id);
      await offlineDb.put(STORES.QUEUE, { ...item, status: QueueStatus.SYNCED });
      this.notifyListeners({ type: 'ITEM_SYNCED', item, result: { idempotent: true } });

      // Nach kurzer Zeit aus Queue entfernen
      setTimeout(async () => {
        await offlineDb.delete(STORES.QUEUE, item.id);
      }, 5000);
      return;
    }

    // Prüfe ob es ein permanenter Fehler ist (kein Retry sinnvoll)
    if (this.isPermanentError(error, item)) {
      console.log('[OfflineQueue] Permanent error - marking as FAILED without retry:', item.id);
      await offlineDb.put(STORES.QUEUE, {
        ...item,
        status: QueueStatus.FAILED,
        error: error.message || 'Permanent error - parent entity may not exist',
        retryCount: MAX_RETRIES
      });
      this.notifyListeners({ type: 'ITEM_FAILED', item, error });

      // Entferne nach kurzer Zeit aus der Queue
      setTimeout(async () => {
        await offlineDb.delete(STORES.QUEUE, item.id);
        console.log('[OfflineQueue] Removed failed item from queue:', item.id);
      }, 3000);
      return;
    }

    const newRetryCount = (item.retryCount || 0) + 1;

    if (newRetryCount >= MAX_RETRIES) {
      // Max Retries erreicht - als FAILED markieren
      await offlineDb.put(STORES.QUEUE, {
        ...item,
        status: QueueStatus.FAILED,
        error: error.message,
        retryCount: newRetryCount
      });
      this.notifyListeners({ type: 'ITEM_FAILED', item, error });
    } else {
      // Retry später
      await offlineDb.put(STORES.QUEUE, {
        ...item,
        status: QueueStatus.PENDING,
        retryCount: newRetryCount,
        nextRetryAt: Date.now() + RETRY_DELAYS[newRetryCount - 1]
      });

      // Retry nach Delay
      const delay = RETRY_DELAYS[newRetryCount - 1];
      console.log(`[OfflineQueue] Retry ${newRetryCount}/${MAX_RETRIES} in ${delay}ms`);
      setTimeout(() => this.processQueue(), delay);
    }
  }

  /**
   * Löst einen Konflikt auf
   * @param {number} itemId
   * @param {'LOCAL'|'SERVER'|'MERGE'} strategy
   * @param {object} mergedData - Bei MERGE die zusammengeführten Daten
   */
  async resolveConflict(itemId, strategy, mergedData = null) {
    const item = await offlineDb.get(STORES.QUEUE, itemId);
    if (!item || item.status !== QueueStatus.CONFLICT) {
      throw new Error('Item nicht gefunden oder kein Konflikt');
    }

    const handler = this.apiHandlers.get(item.entityType);
    if (!handler) {
      throw new Error(`Kein API-Handler für EntityType: ${item.entityType}`);
    }

    switch (strategy) {
      case 'SERVER':
        // Server-Version übernehmen - Queue-Item entfernen
        await offlineDb.delete(STORES.QUEUE, itemId);
        // Cache mit Server-Version aktualisieren
        const cacheStore = this.getCacheStoreForEntity(item.entityType);
        if (cacheStore && item.conflict?.serverVersion) {
          await offlineDb.put(cacheStore, item.conflict.serverVersion);
        }
        break;

      case 'LOCAL':
        // Lokale Version erzwingen
        await handler.update(item.entityId, item.payload);
        await offlineDb.delete(STORES.QUEUE, itemId);
        break;

      case 'MERGE':
        // Zusammengeführte Daten speichern
        if (!mergedData) {
          throw new Error('mergedData erforderlich für MERGE');
        }
        await handler.update(item.entityId, mergedData);
        await offlineDb.delete(STORES.QUEUE, itemId);
        break;

      default:
        throw new Error(`Unbekannte Strategie: ${strategy}`);
    }

    this.notifyListeners({ type: 'CONFLICT_RESOLVED', itemId, strategy });
  }

  /**
   * Event Handler für Online-Status
   */
  handleOnline() {
    console.log('[OfflineQueue] Online - starte Sync');
    this.isOnline = true;
    this.notifyListeners({ type: 'ONLINE' });
    this.processQueue();
  }

  /**
   * Event Handler für Offline-Status
   */
  handleOffline() {
    console.log('[OfflineQueue] Offline');
    this.isOnline = false;
    this.notifyListeners({ type: 'OFFLINE' });
  }

  /**
   * Abonniert Queue-Events
   * @param {function} callback
   * @returns {function} Unsubscribe-Funktion
   */
  subscribe(callback) {
    this.syncListeners.add(callback);
    return () => this.syncListeners.delete(callback);
  }

  /**
   * Benachrichtigt alle Listener
   */
  notifyListeners(event) {
    this.syncListeners.forEach(cb => {
      try {
        cb(event);
      } catch (error) {
        console.error('[OfflineQueue] Listener-Fehler:', error);
      }
    });
  }

  /**
   * Holt alle ausstehenden Items
   */
  async getPendingItems() {
    const allItems = await offlineDb.getAll(STORES.QUEUE);
    return allItems.filter(item =>
      item.status === QueueStatus.PENDING ||
      item.status === QueueStatus.SYNCING
    );
  }

  /**
   * Holt alle Konflikte
   */
  async getConflicts() {
    const allItems = await offlineDb.getAll(STORES.QUEUE);
    return allItems.filter(item => item.status === QueueStatus.CONFLICT);
  }

  /**
   * Holt die Anzahl ausstehender Items
   */
  async getPendingCount() {
    const pending = await this.getPendingItems();
    return pending.length;
  }

  /**
   * Erzwingt eine sofortige Synchronisation
   */
  async forceSync() {
    if (!this.isOnline) {
      console.log('[OfflineQueue] Kann nicht synchronisieren - offline');
      return false;
    }

    await this.processQueue();
    return true;
  }

  /**
   * Storniert ausstehende CREATE-Operationen für ein bestimmtes Item
   * Wird verwendet wenn ein Item direkt (online) gelöscht wird
   * @param {string} entityType - Entity-Typ
   * @param {string} listId - Listen-ID
   * @param {string} itemName - Item-Name (optional)
   * @returns {Promise<number>} Anzahl stornierter Operationen
   */
  async cancelPendingCreatesForItem(entityType, listId, itemName = null) {
    const allItems = await offlineDb.getAll(STORES.QUEUE);
    let cancelledCount = 0;

    for (const item of allItems) {
      if (item.status !== QueueStatus.PENDING && item.status !== QueueStatus.FAILED) {
        continue;
      }

      if (item.operation !== OperationType.CREATE) {
        continue;
      }

      if (item.entityType !== entityType) {
        continue;
      }

      // Prüfe ob es dieselbe Liste betrifft
      const itemListId = String(item.payload?.listId || item.parentId || '');
      if (itemListId !== String(listId)) {
        continue;
      }

      // Wenn Item-Name angegeben, prüfe auch den Namen
      if (itemName) {
        const queuedItemName = item.payload?.item?.name;
        if (queuedItemName && queuedItemName.toLowerCase() !== itemName.toLowerCase()) {
          continue;
        }
      }

      console.log('[OfflineQueue] Storniere ausstehende CREATE-Operation:', item.id);
      await offlineDb.delete(STORES.QUEUE, item.id);
      this.notifyListeners({ type: 'ITEM_CANCELLED', item, reason: 'Item was deleted directly' });
      cancelledCount++;
    }

    return cancelledCount;
  }

  /**
   * Leert die gesamte Queue (für Logout/Reset)
   */
  async clearQueue() {
    await offlineDb.clear(STORES.QUEUE);
    this.notifyListeners({ type: 'QUEUE_CLEARED' });
  }

  /**
   * Bereinigt verwaiste Queue-Items (mit temp-IDs die nie synchronisiert wurden)
   * Sollte beim App-Start aufgerufen werden
   */
  async cleanupOrphanedItems() {
    const allItems = await offlineDb.getAll(STORES.QUEUE);
    let removedCount = 0;

    for (const item of allItems) {
      const hasOrphanedTempId =
        (item.parentId && String(item.parentId).startsWith('temp-')) ||
        (item.payload?.listId && String(item.payload.listId).startsWith('temp-')) ||
        (item.entityId && String(item.entityId).startsWith('temp-') &&
          item.operation !== OperationType.CREATE);

      // Prüfe ob es eine Parent-CREATE Operation gibt
      if (hasOrphanedTempId) {
        const tempId = item.parentId || item.payload?.listId;
        const hasParentCreate = tempId && allItems.some(
          other => other.tempId === tempId &&
                   other.operation === OperationType.CREATE &&
                   other.status !== QueueStatus.FAILED
        );

        if (!hasParentCreate) {
          console.log('[OfflineQueue] Removing orphaned item:', item.id, 'with temp ref:', tempId);
          await offlineDb.delete(STORES.QUEUE, item.id);
          removedCount++;
        }
      }

      // Entferne auch alte FAILED Items (älter als 1 Stunde)
      if (item.status === QueueStatus.FAILED) {
        const oneHourAgo = Date.now() - (60 * 60 * 1000);
        if (item.timestamp < oneHourAgo) {
          console.log('[OfflineQueue] Removing old failed item:', item.id);
          await offlineDb.delete(STORES.QUEUE, item.id);
          removedCount++;
        }
      }
    }

    if (removedCount > 0) {
      console.log(`[OfflineQueue] Cleaned up ${removedCount} orphaned/failed items`);
      this.notifyListeners({ type: 'QUEUE_CLEANUP', removedCount });
    }

    return removedCount;
  }
}

// Singleton-Instanz exportieren
export const offlineQueueService = new OfflineQueueService();
export default offlineQueueService;
